import { ChangeEvent, useRef, useEffect, useState } from "react";
import { Icon } from "@zapier/zinnia-icons";
import { Button, Link, Text } from "@zapier/design-system";
import { CopilotHeader } from "src/components/CopilotPage/CopilotHeader/CopilotHeader";
import styles from "./CopilotPage.module.css";
import { CopilotInput } from "@zapier/design-system-ai";
import {
  UserInteractionEventName,
  emitUserInteractionEvent,
} from "src/observability/tracking";
import { UIMessage } from "ai";
import { getReadableTime } from "src/utils/date";
import { useTemplateCreation } from "@zapier/templates";
import { type Template } from "@zapier/templates";
import { getSuggestions } from "src/lib/actions";

import { CopilotMessages } from "../CopilotMessages";

type CopilotPageProps = {
  template: Template;
  input: string;
  handleInputChange: (input: ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: () => void;
  setInput: (input: string) => void;
  messages: UIMessage[];
  setIsCopilotOpen: (isCopilotOpen: boolean) => void;
  editorUrl: string;
  tryTemplateActions: TryTemplateActions;
};

type TryTemplateActions = {
  hasCreationScreen: boolean;
  createTemplate: (templateKey: string) => void;
  creationError: string | null;
  setCreationError: (error: string | null) => void;
};

export const CopilotPage = ({
  template,
  input,
  handleInputChange,
  handleSubmit,
  messages,
  setIsCopilotOpen,
  editorUrl,
  tryTemplateActions,
}: CopilotPageProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    // only when last message is not visible scroll so that we don't have constant scrolling which is hard on the eyes
    if (lastMessageRef.current) {
      const rect = lastMessageRef.current.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages]);

  const lastAssistantIdxRef = useRef<number>(-1);

  useEffect(() => {
    // Get 3 suggestion options
    const lastIdx = messages.length - 1;
    const lastMsg = messages[lastIdx];

    // Only when the most-recent message is assistant and we haven't fetched suggestions for this turn yet
    if (
      !lastMsg ||
      lastMsg.role !== "assistant" ||
      lastAssistantIdxRef.current === lastIdx
    )
      return;

    // Mark this turn as done before the async call to avoid race conditions if messages mutates quickly
    lastAssistantIdxRef.current = lastIdx;

    setLoadingSuggestions(true);
    getSuggestions(messages)
      .then(setSuggestions)
      .catch(() => setSuggestions(null))
      .finally(() => setLoadingSuggestions(false));
  }, [messages.length]);

  const onSubmit = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotSubmitted,
      {
        event_action: "click",
        option_selected: `Submitted prompt: ${input}`,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    handleSubmit();
  };

  const { createTemplate, renderCreationUI, createTemplateAndNavigate } =
    useTemplateCreation({
      tryTemplateActions,
      webIntentUrl: editorUrl,
      slug: template.slug,
      products: template.products?.items,
    });

  const handleSuggestionClick = (suggestion: string) => {
    handleInputChange({
      target: {
        value: suggestion,
      },
    } as ChangeEvent<HTMLTextAreaElement>);
    inputRef.current?.focus();
  };

  return (
    <div className={styles.page}>
      {renderCreationUI()}
      <div>
        <Button
          iconPosition="start"
          variant="ghost"
          onPress={() => setIsCopilotOpen(false)}
        >
          <Icon name="arrowSmallLeft" size={20} /> Back to details
        </Button>
        <div className={styles.summaryCard}>
          <CopilotHeader
            templateName={template.name}
            templateImageUrl={template.previewImage?.url}
            firstMessageAgo={getReadableTime(messages[0]?.createdAt)}
          />
        </div>

        <div className={styles.messageList}>
          <CopilotMessages messages={messages} />
        </div>
      </div>
      <div>
        <div className={styles.inputRow}>
          {suggestions && !loadingSuggestions && (
            <div className={styles.suggestionChips}>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  onClick={() => handleSuggestionClick(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSuggestionClick(s);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={styles.suggestionChip}
                >
                  <Text type="SmallPrint2" color="TextWeakest">
                    {s}
                  </Text>
                </div>
              ))}
            </div>
          )}
          {loadingSuggestions && (
            <Text type="SmallPrint2" color="TextWeakest">
              Thinking of responses…
            </Text>
          )}
          <div className={styles.editorLink}>
            <Link
              onClick={() =>
                createTemplateAndNavigate({
                  slug: template.slug,
                  webIntentUrl: editorUrl,
                  createTemplate,
                })
              }
            >
              <Text type="Body2" color="UiPrimaryStronger">
                Try this template
              </Text>
            </Link>
            <Icon name="arrowOffsite" size={16} color="UiPrimaryStronger" />
          </div>
        </div>

        <CopilotInput
          ref={inputRef}
          input={input}
          handleInputChange={handleInputChange}
          handleSubmit={onSubmit}
          placeholder="Ask about this template…"
          className={styles.copilotInput}
        />
      </div>
    </div>
  );
};
